import React, { useState, useEffect } from 'react';
import { toAbsoluteUrl } from '../../helpers/AssetHelpers';

import './Image.css';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ImageProps {
  imageBase64?: string;
  imageUrl?: string;
  imageName?: string;
  classes?: string[];
  attributes?: React.ImgHTMLAttributes<HTMLImageElement>;
  dataId?: string | null;
  fontAwesomeIcon?: keyof typeof icons;
}

const Image: React.FC<ImageProps> = ({
  imageBase64,
  imageUrl,
  imageName,
  classes = [],
  attributes,
  dataId,
  fontAwesomeIcon,
}) => {
  const [imgError, setImgError] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Check if browser is Safari or iOS WebKit
    const safariCheck =
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsSafari(safariCheck);
  }, []);

  if (fontAwesomeIcon) {
    return (
      <FontAwesomeIcon
        icon={icons[fontAwesomeIcon] as IconProp}
        className={classes.join(' ') + ' ui-widget-fontawesome-icon'}
      />
    );
  }

  let src: string | undefined;

  if (imageBase64) {
    src = imageBase64;
  } else if (imageUrl) {
    src = toAbsoluteUrl(imageUrl);
  } else if (imageName) {
    // For SVG files, use PNG for Safari/iOS
    if (imageName.toLowerCase().endsWith('svg')) {
      if (isSafari) {
        src = `/images/${imageName.replace('.svg', '.png')}`;
      } else {
        src = `/images/${imageName}`;
      }
    } else if (isSafari && imageName.toLowerCase().endsWith('png')) {
      // If PNG is explicitly requested, use it as is
      src = `/images/${imageName}`;
    } else {
      // Default case - use SVG for non-Safari, but PNG for Safari
      src = isSafari ? `/images/${imageName}.png` : `/images/${imageName}.svg`;
    }
  }

  // Return null if src is undefined, null, or an empty string
  if (!src) {
    return null;
  }

  // No need for special object tag handling since we're using PNG for Safari
  return (
    <span className={`ui-image-container ${classes.join(' ')}`}>
      <img
        className="ui-image"
        src={src}
        data-id={dataId}
        {...attributes}
        onError={e => {
          // If image fails, try alternative format
          if (src?.endsWith('.svg')) {
            (e.target as HTMLImageElement).src = src.replace('.svg', '.png');
          } else if (src?.endsWith('.png')) {
            (e.target as HTMLImageElement).src = src.replace('.png', '.svg');
          } else {
            setImgError(true);
          }
        }}
      />
    </span>
  );
};

export default Image;
